import React, { useMemo, useCallback, useState } from 'react';
import _partition from 'lodash/partition';

// Components
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import ListItemText from '@material-ui/core/ListItemText';
import TextField from '@material-ui/core/TextField';

// Styles
import withStyles from '@material-ui/core/styles/withStyles';
import { defineStyles, Classes } from 'styles/defineStyles';
import classNames from 'classnames';
import { REQUIRED_PROFILE_ATTR_KEYS } from 'pages/Directory/consts';

const styles = defineStyles(theme => {
  const ITEM_HEIGHT = 48;

  return {
    row: {
      padding: theme.spacing(1, 1, 0, 1),
    },
    dividerLine: {
      width: '100%',
      height: 1,
      backgroundColor: theme.palette.text.secondary,
    },
    menuListRoot: {
      maxHeight: ITEM_HEIGHT * 5.5,
      width: 400,
      overflowY: 'auto',
    },
    searchInput: {
      width: '100%',
    },
    subField: {
      marginLeft: theme.spacing(3),
    },
  };
});

interface Props {
  onSelectAttribute: (attribute: any) => void;
  attributes: any[];
  isPrimary: boolean;
  toggleCreateAttributeDialog: () => any;
  classes: Classes<typeof styles>;
}

const AttributeMenu = (props: Props) => {
  const { classes, attributes, onSelectAttribute, isPrimary } = props;
  const [search, setSearch] = useState('');

  const menuItems = useMemo(() => {
    const mappableAttributes = attributes.filter(attribute => attribute.mappable);
    const list = mappableAttributes.filter(attribute => {
      const displayName = attribute.displayName ? attribute.displayName.toLowerCase() : '';
      return displayName.includes(search.toLowerCase());
    });

    const menuItems = list.sort((a, b) => (a.displayName > b.displayName ? 1 : -1));
    return menuItems;
  }, [search, attributes]);

  const [requiredItems, _otherItems] = _partition(menuItems, m =>
    REQUIRED_PROFILE_ATTR_KEYS.includes(m.objectKey),
  );

  const [emailAttr, otherAttrs] = _partition(_otherItems, attr => attr.objectKey === 'email');
  const otherItems = [{ ...emailAttr[0], displayName: 'Email (Recommended)' }, ...otherAttrs];

  const handleSearch = useCallback(e => setSearch(e.target.value), [setSearch]);

  return (
    <>
      <div>
        <div className={classes.dividerLine} />
        <div className={classes.row}>
          <TextField className={classes.searchInput} placeholder="Search" onChange={handleSearch} />
        </div>
        <MenuList className={classNames(classes.row, classes.menuListRoot)}>
          {isPrimary && (
            <>
              <div>Required Attributes</div>
              {requiredItems.map((attribute: any, index: number) => (
                <MenuItem
                  onClick={e => onSelectAttribute(attribute)}
                  disabled={
                    !!attribute.mappedToSource || attribute.isObject || attribute.alreadySelected
                  }
                  key={index}
                  data-cy={`${attribute.displayName}-required-menu-item`}
                >
                  <ListItemText
                    className={attribute.fromAttribute ? classes.subField : ''}
                    primary={`${attribute.displayName} (Required)`}
                    secondary={
                      attribute.mappedToSource
                        ? `Already mapped to ${attribute.mappedToSource}`
                        : ''
                    }
                  />
                </MenuItem>
              ))}
              <div>Other Attributes</div>
            </>
          )}

          {otherItems.map((attribute: any, index: number) => (
            <MenuItem
              onClick={e => onSelectAttribute(attribute)}
              disabled={
                !!attribute.mappedToSource || attribute.isObject || attribute.alreadySelected
              }
              key={index}
              data-cy={`${attribute.displayName}-other-menu-item`}
            >
              <ListItemText
                className={attribute.fromAttribute ? classes.subField : ''}
                primary={attribute.displayName}
                secondary={
                  attribute.mappedToSource ? `Already mapped to ${attribute.mappedToSource}` : ''
                }
              />
            </MenuItem>
          ))}
        </MenuList>
      </div>
    </>
  );
};

export default withStyles(styles)(AttributeMenu);
